import React, { useState, useEffect } from 'react';
import { fetchTournaments, fetchTournamentParticipants } from '../../api/TournamentsApi';
import TournamentCard from '../tournament/TournamentCard';
import TournamentStagesModal from '../tournament/TournamentStagesModal';
import TournamentFilters from '../tournament/TournamentFilters';
import SoloTournamentRegistrationModal from '../tournament/SoloTournamentRegistrationModal';
import TeamTournamentRegistrationModal from '../tournament/TeamTournamentRegistrationModal';
import TournamentParticipantsModal from '../tournament/TournamentParticipantsModal';
import ChatRoomModal from '../tournament/ChatRoomModal';


const PlayerTournaments = ({ upcomingMatches, recentResults, formatDate }) => {
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTournamentId, setSelectedTournamentId] = useState(null);
  
  // Solo registration modal state
  const [isSoloModalOpen, setIsSoloModalOpen] = useState(false);
  
  // Participants modal state
  const [isParticipantsModalOpen, setIsParticipantsModalOpen] = useState(false);
  
  // Chat modal state
  const [isChatModalOpen, setIsChatModalOpen] = useState(false);
  const [selectedTournamentForChat, setSelectedTournamentForChat] = useState(null);
  
  // Filter states
  const [filters, setFilters] = useState({
    game: "all",
    tournamentType: "all",
    entryFeeType: "all",
    status: "all",
    registered: "all"
  });
  
  const [filteredTournaments, setFilteredTournaments] = useState([]);
  
  // Add state for storing registration details
  const [playerRegistrations, setPlayerRegistrations] = useState({});
  
  // Add state for stages modal
  const [isStagesModalOpen, setIsStagesModalOpen] = useState(false);
  const [selectedTournamentIdForStages, setSelectedTournamentIdForStages] = useState(null);
  const [selectedTournamentNameForStages, setSelectedTournamentNameForStages] = useState('');
  
  // Add state for current player ID
  const [currentPlayerId, setCurrentPlayerId] = useState(null);
  
  // Get current player ID from localStorage
  useEffect(() => {
    try {
      const userInfo = JSON.parse(localStorage.getItem('user_info') || '{}');
      setCurrentPlayerId(userInfo.id);
    } catch (error) {
      console.error("Error getting player ID from localStorage:", error);
    }
  }, []);
  
  // Format date for tournament display
  const formatTournamentDate = (dateString) => {
    const options = { month: 'short', day: 'numeric', year: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };
  
  // Calculate days remaining for registration
  const getDaysRemaining = (endDate) => {
    const now = new Date();
    const end = new Date(endDate);
    const diffTime = end - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays <= 0) {
      return "Closing today";
    } else if (diffDays === 1) {
      return "1 day left";
    } else {
      return `${diffDays} days left`;
    }
  };
  
  // Fetch tournaments on component mount
  useEffect(() => {
    const loadTournaments = async () => {
      try {
        setLoading(true); // Ensure loading state is set only once at the start
        const shouldFetchCompletedOnly = filters.status === "completed";
        const data = await fetchTournaments({
          excludeCompleted: !shouldFetchCompletedOnly,
          onlyCompleted: shouldFetchCompletedOnly
        });
        setTournaments(data);
        setFilteredTournaments(data);
        setError(null);
      } catch (err) {
        setError("Failed to load tournaments. Please try again later.");
      } finally {
        setLoading(false); // Ensure loading state is cleared after data is fetched
      }
    };
    
    loadTournaments();
  }, [filters.status]);

  // New effect to fetch registration details for tournaments where user is registered
  useEffect(() => {
    const fetchRegistrationDetails = async () => {
      const registrationDetails = {};
      
      for (const tournament of tournaments) {
        if (tournament.is_registered) {
          try {
            const data = await fetchTournamentParticipants(tournament.id);
            // Find the current player's registration among participants
            const currentUserData = data.registered_players?.find(
              player => player.is_current_user
            );
            
            if (currentUserData) {
              registrationDetails[tournament.id] = currentUserData;
            }
          } catch (err) {
            console.error(`Failed to load registration details for tournament ${tournament.id}:`, err);
          }
        }
      }
      
      setPlayerRegistrations(registrationDetails);
    };
    
    if (tournaments.length > 0 && tournaments.some(t => t.is_registered)) {
      fetchRegistrationDetails();
    }
  }, [tournaments]);
  
  // Fetch tournaments when filters change
  useEffect(() => {
    const loadTournaments = async () => {
      try {
        setLoading(true);
        
        // Check if "completed" filter is selected
        const shouldFetchCompletedOnly = filters.status === "completed";
        
        // console.log(`Fetching tournaments with filter: status=${filters.status}, fetchCompletedOnly=${shouldFetchCompletedOnly}`);
        
        // Using optimized fetch - fetching appropriate tournaments based on filter
        const data = await fetchTournaments({
          excludeCompleted: !shouldFetchCompletedOnly,
          onlyCompleted: shouldFetchCompletedOnly
        });
        
        // console.log(`Received ${data.length} tournaments from API`);
        setTournaments(data);
        setFilteredTournaments(data);
        setError(null);
      } catch (err) {
        // console.error("Failed to load tournaments:", err);
        setError("Failed to load tournaments. Please try again later.");
      } finally {
        setLoading(false);
      }
    };
    
    loadTournaments();
  }, [filters.status]); // Dependency on status filter ensures proper API call

  // Apply filters when tournaments or filters change
  useEffect(() => {
    const filtered = tournaments.filter(tournament => {
      // Game filter
      if (filters.game !== "all" && tournament.game.toLowerCase() !== filters.game.toLowerCase()) {
        return false;
      }
      
      // Tournament type filter
      if (filters.tournamentType !== "all" && tournament.tournament_type !== filters.tournamentType) {
        return false;
      }
      
      // Entry fee type filter
      if (filters.entryFeeType !== "all" && tournament.entry_fee_type !== filters.entryFeeType) {
        return false;
      }
      
      // Status filter - skip for completed since it's handled by backend
      if (filters.status !== "all" && filters.status !== "completed" && tournament.tournament_status !== filters.status) {
        return false;
      }
      
      // Registration filter
      if (filters.registered !== "all") {
        const isRegistered = tournament.is_registered || false;
        if (filters.registered === "registered" && !isRegistered) {
          return false;
        }
        if (filters.registered === "not_registered" && isRegistered) {
          return false;
        }
      }
      
      return true;
    });
    
    setFilteredTournaments(filtered);
    // console.log(`Applied frontend filters: ${filtered.length} tournaments after filtering`);
  }, [filters, tournaments]);
  
  // Handle filter changes
  const handleFilterChange = (filterType, value) => {
    setFilters({
      ...filters,
      [filterType]: value
    });
  };
  
  // Reset all filters
  const resetFilters = () => {
    setFilters({
      game: "all",
      tournamentType: "all",
      entryFeeType: "all",
      status: "all",
      registered: "all"
    });
  };
  
  // Handle opening the solo registration modal
  const openSoloRegistration = (tournamentId) => {
    setSelectedTournamentId(tournamentId);
    setIsSoloModalOpen(true);
  };

  // Add this function to open the participants modal directly from the tournament card
  const openParticipantsModal = (tournamentId, tournamentType) => {
    setSelectedTournamentId(tournamentId);
    setSelectedTournamentType(tournamentType);
    setIsParticipantsModalOpen(true);
  };

  // Add function to open chat modal
  const openChatModal = (tournament) => {
    setSelectedTournamentForChat(tournament);
    setIsChatModalOpen(true);
  };

  // Add the openStagesModal function
  const openStagesModal = (tournamentId, tournamentName) => {
    // // console.log("openStagesModal called in PlayerTournaments with:", tournamentId, tournamentName);
    setSelectedTournamentIdForStages(tournamentId);
    setSelectedTournamentNameForStages(tournamentName);
    setIsStagesModalOpen(true);
  };

  // Add team registration modal state
  const [isTeamModalOpen, setIsTeamModalOpen] = useState(false);
  
  // Add team registration handler
  const openTeamRegistration = (tournamentId, game) => {
    setSelectedTournamentId(tournamentId);
    setIsTeamModalOpen(true);
  };

  // Add state for selected tournament type
  const [selectedTournamentType, setSelectedTournamentType] = useState(null);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
        <p className="font-bold">Error</p>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Tournament Filters */}
      <TournamentFilters filters={filters} onFilterChange={handleFilterChange} onReset={resetFilters} />
      {/* Tournament Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredTournaments.map((tournament) => (
          <TournamentCard
            key={tournament.id}
            tournament={tournament}
            playerRegistrations={playerRegistrations}
            formatTournamentDate={formatTournamentDate}
            getDaysRemaining={getDaysRemaining}
            openParticipantsModal={openParticipantsModal}
            openChatModal={openChatModal}
            openSoloRegistration={openSoloRegistration}
            openTeamRegistration={openTeamRegistration}
            openStagesModal={openStagesModal}
            currentPlayerId={currentPlayerId} // Pass the current player ID
          />
        ))}
      </div>
      {/* Modals */}
      <SoloTournamentRegistrationModal
        isOpen={isSoloModalOpen}
        onClose={() => setIsSoloModalOpen(false)}
        tournamentId={selectedTournamentId}
        game={filteredTournaments.find((t) => t.id === selectedTournamentId)?.game}
        isRegistered={filteredTournaments.find((t) => t.id === selectedTournamentId)?.is_registered}
      />
      <TeamTournamentRegistrationModal
        isOpen={isTeamModalOpen}
        onClose={() => setIsTeamModalOpen(false)}
        tournamentId={selectedTournamentId}
        game={filteredTournaments.find((t) => t.id === selectedTournamentId)?.game}
        isRegistered={filteredTournaments.find((t) => t.id === selectedTournamentId)?.is_registered}
      />
      <TournamentParticipantsModal
        isOpen={isParticipantsModalOpen}
        onClose={() => setIsParticipantsModalOpen(false)}
        tournamentId={selectedTournamentId}
        tournamentType={selectedTournamentType}
      />
      <ChatRoomModal
        isOpen={isChatModalOpen}
        onClose={() => setIsChatModalOpen(false)}
        tournamentId={selectedTournamentForChat?.id}
        tournamentName={selectedTournamentForChat?.name}
      />
      <TournamentStagesModal
        isOpen={isStagesModalOpen}
        onClose={() => setIsStagesModalOpen(false)}
        tournamentId={selectedTournamentIdForStages}
        tournamentName={selectedTournamentNameForStages}
      />
    </div>
  );
};

export default PlayerTournaments;

